import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Target, FlaskConical, Users } from "lucide-react";
import { SEO } from "../components/SEO";

const PERSONAS = [
  "Freelance writers & copywriters wanting to write faster without losing quality",
  "Digital marketers & social media managers who need content on tight deadlines",
  "Ecommerce entrepreneurs writing product descriptions and email sequences at scale",
  "LinkedIn creators and personal brand builders",
  "Agencies and small marketing teams juggling multiple writers and brand voices",
];

export default function About() {
  return (
    <>
      <SEO title="About AI Writing Platforms" description="Why we built AI Writing Platforms, who it's for, and exactly how we test every AI writing tool we review." />
      <section className="pt-36 pb-20 px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-mono-tag text-xs uppercase tracking-[0.2em] text-gold mb-4">About</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="font-display font-black text-4xl sm:text-5xl text-white tracking-tight">Hands-On Reviews, Not Marketing Copy</motion.h1>
          <p className="text-zinc-400 mt-6 text-lg leading-relaxed">AI Writing Platforms exists because most "best AI tool" lists are written without ever opening the tool. We test every product on this site with a paid account, real writing tasks, and a published scoring rubric — then we tell you what actually happened, including the parts that didn't work.</p>

          <img src="https://images.unsplash.com/photo-1598281944140-e3673d72513c?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Writer working late testing AI writing tools" className="w-full h-72 object-cover rounded-sm border border-white/10 mt-10" />

          <div className="grid sm:grid-cols-3 gap-6 mt-14">
            {[
              { icon: Target, title: "Our Mission", text: "Help writers, marketers, and entrepreneurs pick the right AI writing tool the first time, based on real testing." },
              { icon: FlaskConical, title: "Our Method", text: "Same 5 test prompts, same 100-point rubric, minimum 12 days of real use before we publish a rating." },
              { icon: Users, title: "Who We're For", text: "Anyone who writes for a living or a business, and doesn't have time to test five subscriptions themselves." },
            ].map((b) => (
              <div key={b.title}>
                <b.icon className="w-6 h-6 text-primary mb-3" aria-hidden="true" />
                <h3 className="font-display font-bold text-white">{b.title}</h3>
                <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>

          <h2 className="font-display font-bold text-2xl text-white mt-16">Who We Write For</h2>
          <ul className="mt-6 space-y-3">
            {PERSONAS.map((p) => (
              <li key={p} className="flex gap-3 text-zinc-300 text-base leading-relaxed">
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" aria-hidden="true" />{p}
              </li>
            ))}
          </ul>

          <h2 className="font-display font-bold text-2xl text-white mt-16">Transparency About Testing</h2>
          <p className="text-zinc-300 mt-4 leading-relaxed">Every review on this site lists exactly how long we tested the tool and the date it was last tested, since AI products change fast. We purchase every subscription ourselves before writing a review. This site earns affiliate commissions when you sign up through our links — see our <Link to="/affiliate-disclosure" className="text-primary hover:text-[#5C7CFA]">Affiliate Disclosure</Link> for full details.</p>
        </div>
      </section>
    </>
  );
}
